import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { referralApi, type ReferralCodeOut } from '@/api/referral';
import { useAuthStore } from '@/store/auth';
import {
  BOW_LOCKUP_SRC,
  CodeAppliedLine,
  ConviteCta,
  ConviteGhost,
  ConviteLandingCard
} from '@/components/referral/ConviteViews';
import { GiftOutline } from '@/components/referral/icons';
import { normalizeReferralCode, setPendingReferralCode } from '@/lib/referral';

/**
 * Landing pública `/convite/:code` — destino do link/QR de indicação
 * (Indique e Ganhe). Pública (sem RequireAuth), sem AppLayout. Design "Bow
 * Convite do Indicado" (Claude Design · bow-convite.jsx · ConviteLanding,
 * variante corrigida): lockup oficial no topo, card navy sólido com brilho
 * azul interno ("Convite de {nome} / Ganhe R$ X / de cashback no primeiro
 * aporte" + "Criar conta"), ghost "Já tenho conta" e a linha "✓ Código X
 * aplicado" — o código fica guardado (localStorage) e é aplicado depois do
 * login pelo `<ReferralClaimer />` (que ainda pergunta antes).
 *
 * Guarda o código como PENDENTE e:
 *  - deslogado → mostra o convite e manda criar conta / entrar;
 *  - logado → vai direto pra Home: o ReferralClaimer pergunta e aplica.
 */

type State = 'loading' | 'ok' | 'invalid' | 'disabled' | 'error';

const DISPLAY = 'Chillax, system-ui, sans-serif';

export default function Convite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const compact = normalizeReferralCode(code);
  const displayCode = (code ?? '').trim().toUpperCase();

  const [state, setState] = useState<State>(compact ? 'loading' : 'invalid');
  const [info, setInfo] = useState<ReferralCodeOut | null>(null);

  useEffect(() => {
    if (!compact) return;
    setPendingReferralCode(compact);
    if (token) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    referralApi
      .lookup(compact)
      .then((r) => {
        if (cancelled) return;
        setInfo(r);
        setState(r.valid ? 'ok' : 'invalid');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState(err instanceof ApiError && err.status === 503 ? 'disabled' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [compact, token, navigate]);

  const goRegister = () =>
    navigate('/register', { state: { from: { pathname: '/' } } });
  const goLogin = () => navigate('/login', { state: { from: { pathname: '/' } } });

  return (
    <div
      className="mx-auto flex min-h-dvh max-w-md flex-col bg-bow-cream"
      style={{
        padding: '0 26px',
        paddingTop: 'max(2.25rem, calc(env(safe-area-inset-top) + 0.75rem))',
        paddingBottom: 'max(28px, env(safe-area-inset-bottom))'
      }}
    >
      <div className="flex-shrink-0">
        <img src={BOW_LOCKUP_SRC} alt="Bow" className="block w-auto" style={{ height: 40 }} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-center">
        {state === 'loading' && (
          <div className="flex flex-col gap-3">
            <div className="h-[300px] w-full animate-pulse rounded-[30px] bg-bow-royal/10" />
            <div className="mx-auto h-4 w-40 animate-pulse rounded bg-bow-royal/10" />
          </div>
        )}

        {state === 'ok' && info && (
          <>
            <ConviteLandingCard
              referrerFirstName={info.referrer_first_name ?? 'Alguém'}
              welcomeBrl={info.welcome_brl ?? '0'}
              onCreateAccount={goRegister}
            />
            <div className="mt-5">
              <ConviteGhost onClick={goLogin}>Já tenho conta</ConviteGhost>
            </div>
            <div className="mt-[18px] flex justify-center">
              <CodeAppliedLine code={displayCode} />
            </div>
          </>
        )}

        {state === 'invalid' && (
          <FallbackCard
            title="Este convite não é válido."
            text="Confira o código com quem te indicou — ou crie sua conta e comece a carregar mesmo assim."
            onCreateAccount={goRegister}
            onLogin={goLogin}
          />
        )}

        {state === 'disabled' && (
          <FallbackCard
            title="O programa de indicação está em pausa."
            text="Guardamos seu código — se o programa voltar, ele é aplicado sozinho. Enquanto isso, dá pra criar sua conta e carregar normalmente."
            onCreateAccount={goRegister}
            onLogin={goLogin}
            code={displayCode}
          />
        )}

        {state === 'error' && (
          <FallbackCard
            title="Não foi possível carregar o convite."
            text="Verifique sua conexão e tente de novo. Seu código ficou guardado."
            primaryLabel="Tentar de novo"
            onCreateAccount={() => window.location.reload()}
            onLogin={goRegister}
            loginLabel="Criar conta mesmo assim"
            code={displayCode}
          />
        )}
      </div>
    </div>
  );
}

/** Estados sem convite válido — mesma linguagem (navy sólido), sem o valor. */
function FallbackCard({
  title,
  text,
  code,
  primaryLabel = 'Criar conta',
  loginLabel = 'Já tenho conta',
  onCreateAccount,
  onLogin
}: {
  title: string;
  text: string;
  code?: string;
  primaryLabel?: string;
  loginLabel?: string;
  onCreateAccount: () => void;
  onLogin: () => void;
}) {
  return (
    <>
      <div
        className="relative overflow-hidden text-bow-cream"
        style={{
          borderRadius: 30,
          background: '#001B74',
          padding: '34px 26px 30px',
          boxShadow: '0 14px 34px rgba(0,27,116,0.20)'
        }}
      >
        <div className="relative flex flex-col items-center text-center">
          <GiftOutline size={64} onDark />
          <div
            className="mt-5 text-white"
            style={{
              fontFamily: DISPLAY,
              fontWeight: 600,
              fontSize: 26,
              letterSpacing: '-0.03em',
              lineHeight: 1.05
            }}
          >
            {title}
          </div>
          <p className="mt-2.5 text-[14.5px] leading-relaxed" style={{ color: 'rgba(241,237,228,0.82)' }}>
            {text}
          </p>
          <div className="mt-6 w-full">
            <ConviteCta onClick={onCreateAccount}>{primaryLabel}</ConviteCta>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <ConviteGhost onClick={onLogin}>{loginLabel}</ConviteGhost>
      </div>
      {code && (
        <div className="mt-[18px] flex justify-center">
          <CodeAppliedLine code={code} />
        </div>
      )}
    </>
  );
}
